import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ReactElement } from 'react';
import { tokens } from '@synergy/theme-palo';

import { useAppSelector } from '../../../store/use-appstate-selector';

interface PaloPrintCountProps {
    /**
     * What cuts the text, where it is not the position text in the PDF — the
     * article name is cut by the price information, not by the document.
     */
    cutBy?: 'pdf' | 'priceInformation';
    /** How long the text is that the print will carry. */
    length: number;
    /**
     * The limit to count against. Left out, the one the PDF uses for position
     * texts applies — it comes from the settings, not from here (FRM-Z).
     */
    limit?: number;
}

/**
 * How much of a text the print will carry, under the field it is written in.
 *
 * The PDF cuts a position text at 255 characters and an article name at 55,
 * and until now nothing said so: a description was written, saved, printed
 * and only then found to be missing its end (#2225). Nothing is blocked here
 * — the mask only says what will be left out.
 */
export const PaloPrintCount = ({ length, limit, cutBy = 'pdf' }: PaloPrintCountProps): ReactElement | null => {
    const fromSettings = useAppSelector(state => state.settings.printCharacterLimit);
    const against = limit ?? fromSettings;

    if (!against) {
        return null;
    }

    const over = length > against;

    return (
        <Box sx={{ mt: '4px', fontSize: '11.5px', color: over ? tokens.color.crit : tokens.color.ink3 }}>
            <FormattedMessage id="app.common.characterLimit" values={{ value: length, total: against }} />
            {over && (
                <Box component="span" sx={{ ml: '8px', fontWeight: 600 }}>
                    <FormattedMessage
                        id={'pdf' === cutBy ? 'app.freePosition.printCut' : 'app.product.nameCut'}
                        values={{ total: against }}
                    />
                </Box>
            )}
        </Box>
    );
};
